import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { cn } from '../../lib/utils';

/**
 * Coarse-pointer tap target (44px), reserved as real layout — never bled.
 *
 * The visible track is 44x24, so on touch it falls 20px short vertically. The fix
 * has two halves that MUST stay in lockstep:
 *
 *  - `touch:my-2.5` reserves 10px above and below the track, growing the switch's
 *    own layout box to 44px. A `DataTable` cell (`px-4 py-1`) simply gets a taller
 *    row on touch devices.
 *  - `touch:after:-inset-y-3` grows the hit area to fill exactly that reserved
 *    space, and no further.
 *
 * The two numbers differ by design. An absolutely-positioned `::after` is offset
 * from its container's PADDING box, and this track carries `border-2`, so 12px of
 * inset lands 10px outside the border box — exactly the reserved margin, for a
 * 24 + 10 + 10 = 44px target. Matching them naively (`-inset-y-2.5`) silently
 * yields a 40px target; changing the border width without changing the inset does
 * the same.
 *
 * An overlay WITHOUT the matching margin is the other trap: it spills ~6px past a
 * `py-1` cell into the neighbouring row, so a tap near a row boundary flips the
 * wrong record's flag — worse than a tap that does nothing.
 * `__tests__/switch.test.tsx` derives the target height from all three numbers and
 * goes red if any of them drifts.
 *
 * Corollary for callers: never resize or `scale-*` a Switch — a transform shrinks
 * the reserved box along with the track and puts the target back under 44px.
 */
const SWITCH_TOUCH_TARGET =
  "touch:my-2.5 touch:after:absolute touch:after:-inset-y-3 touch:after:inset-x-0 touch:after:content-['']";

/**
 * Perceivable boundary — the reason an OFF switch used to disappear.
 *
 * The ON track is `bg-primary` (near-black slate) and needs nothing. The OFF
 * track is `bg-input`, which is `hsl(214 32% 91%)` on a `bg-card` white
 * surface: **1.22:1**, well under WCAG 1.4.11's 3:1 for the boundary of a UI
 * component, and barely a smudge. `disabled:opacity-50` then took that to
 * ~1.1:1 — a control the user genuinely cannot see, which is exactly how the
 * Shopify connection page's four scope-gated toggles read as a blank column.
 *
 * The fix is an OUTLINE, not a darker fill: darkening the off track toward 3:1
 * makes it read as ON. The `border-2` is already in the layout box (it was just
 * `border-transparent`), so making it visible changes no geometry and none of
 * the tap-target arithmetic above. The ON track keeps the base
 * `border-transparent` — an attribute-scoped utility outranks it in the
 * cascade, so only the unchecked state needs to be spelled.
 *
 * `disabled:opacity-70` rather than the shared `disabled:opacity-50`: WCAG
 * exempts disabled controls from contrast, but "inert" must not mean "absent" —
 * 70% keeps the outline at ~2.3:1 on card, plainly visible, while still reading
 * as faded next to an enabled sibling. Both ratios are asserted in
 * `scripts/audit-contrast.ts`.
 */
const SWITCH_BOUNDARY =
  'data-[state=unchecked]:border-switch-border disabled:opacity-70';

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      'peer relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      SWITCH_BOUNDARY,
      SWITCH_TOUCH_TARGET,
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 rtl:data-[state=checked]:-translate-x-5'
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
